import {
  getSolutionsCatalog,
} from '../../../shell/events/solutionsCatalogBridge';
import type { CatalogItemSnapshot } from '../../../shell/events/solutionsCatalogBridge';
import {
  FALLBACK_SOLUTION_CANVAS_MAP,
  buildSolutionCanvasMap,
} from './solutionCanvasMap';

/**
 * Fallback module/canvas → PKS `application_ukey` when the unscoped catalog
 * has not yet published `solution.application_ukey` (e.g. before first fetch).
 * Values match PKS application records (`daxl-app`, `gaxl-app`, `hcaxl-app`).
 */
export const MODULE_APPLICATION_UKEY_MAP: Readonly<Record<string, string>> = {
  'daxl-product-data-management': 'daxl-app',
  'product-data-prep': 'daxl-app',
  'daxl-ontology-builder': 'gaxl-app',
  'patient-risk-intelligence': 'hcaxl-app',
};

const CANVAS_ROUTE_RE = /\/operational\/canvas\/(.+)$/;

function applicationFromCatalogItem(item: CatalogItemSnapshot): string | undefined {
  const app = item.solution?.application_ukey?.trim();
  return app || undefined;
}

/** Look up application_ukey from the unscoped waffle catalog by agent or solution ukey. */
function applicationFromCatalogByAgentOrSolution(id: string): string | undefined {
  const snapshot = getSolutionsCatalog();
  if (!snapshot) return undefined;

  for (const item of snapshot.catalogItems) {
    if (
      item.runtime_agent?.ukey === id ||
      item.solution?.ukey === id
    ) {
      const app = applicationFromCatalogItem(item);
      if (app) return app;
    }
  }
  return undefined;
}

/**
 * Resolve application_ukey for a canvas/waffle module using catalog items that
 * route to that module (via agent→canvas map or moduleSolutionMap).
 */
function applicationFromCatalogByModuleOrCanvas(
  moduleOrCanvasId: string,
): string | undefined {
  const snapshot = getSolutionsCatalog();
  if (!snapshot) return undefined;

  const canvasMap = buildSolutionCanvasMap(snapshot);
  for (const [agentUkey, canvasModule] of Object.entries(canvasMap)) {
    if (
      canvasModule === moduleOrCanvasId ||
      moduleOrCanvasId.startsWith(`${canvasModule}-`)
    ) {
      const app = applicationFromCatalogByAgentOrSolution(agentUkey);
      if (app) return app;
    }
  }

  const solutionUkeys = snapshot.moduleSolutionMap[moduleOrCanvasId] ?? [];
  for (const solUkey of solutionUkeys) {
    const app = applicationFromCatalogByAgentOrSolution(solUkey);
    if (app) return app;
  }

  return undefined;
}

function applicationForModuleOrCanvas(id: string | null | undefined): string | undefined {
  if (!id) return undefined;

  const fromCatalog = applicationFromCatalogByModuleOrCanvas(id);
  if (fromCatalog) return fromCatalog;

  const direct = MODULE_APPLICATION_UKEY_MAP[id];
  if (direct) return direct;

  for (const [moduleKey, applicationUkey] of Object.entries(MODULE_APPLICATION_UKEY_MAP)) {
    if (id === moduleKey || id.startsWith(`${moduleKey}-`)) {
      return applicationUkey;
    }
  }
  return undefined;
}

function canvasModuleForSolution(solutionId: string): string | undefined {
  const liveMap = buildSolutionCanvasMap(getSolutionsCatalog());
  return liveMap[solutionId] ?? FALLBACK_SOLUTION_CANVAS_MAP[solutionId];
}

/**
 * Resolves PKS `application_ukey` for solutions/catalog from shell selection.
 * Prefers `solution.application_ukey` from the unscoped catalog; falls back to
 * MODULE_APPLICATION_UKEY_MAP.
 */
export function resolveCatalogApplicationUkey(args: {
  activeModule?: string | null;
  activeSolution?: string | null;
  activeCanvas?: string | null;
}): string | undefined {
  const solutionId = args.activeSolution?.trim();
  if (solutionId) {
    const fromCatalog = applicationFromCatalogByAgentOrSolution(solutionId);
    if (fromCatalog) return fromCatalog;

    const fromCanvasViaSolution = applicationForModuleOrCanvas(
      canvasModuleForSolution(solutionId),
    );
    if (fromCanvasViaSolution) return fromCanvasViaSolution;
  }

  const fromModule = applicationForModuleOrCanvas(args.activeModule);
  if (fromModule) return fromModule;

  return applicationForModuleOrCanvas(args.activeCanvas);
}

/**
 * Resolve application_ukey from the waffle item being clicked — before shell
 * state updates — so scoped catalog fetch can run immediately on select.
 */
export function resolveCatalogApplicationUkeyFromWaffleItem(item: {
  id: string;
  type: 'module' | 'solution';
  route?: string;
}): string | undefined {
  if (item.type === 'module') {
    return resolveCatalogApplicationUkey({ activeModule: item.id });
  }

  const fromSolution = resolveCatalogApplicationUkey({ activeSolution: item.id });
  if (fromSolution) return fromSolution;

  if (item.route) {
    const match = CANVAS_ROUTE_RE.exec(item.route);
    if (match) {
      return resolveCatalogApplicationUkey({ activeCanvas: match[1] });
    }
  }

  return undefined;
}
